// Sliding indicators: the pill behind the selected option of a segmented
// control, the underline under the active tab and the highlight behind the
// current sidebar item glide to the new selection instead of jumping.
//
// Installed once for the whole app (index.js). It finds every container below
// on its own, so screens keep their plain markup (`ls-segmented` >
// `button[aria-pressed]`, `ls-tabs` > `ls-tab.is-active`, ...) and need no
// wrapper component. The indicator is the container's own ::before / ::after
// (shell.css, billing.css), positioned from CSS custom properties set here, so
// React re-renders can never remove it:
//   --ind-x / --ind-y  the active item's offset inside the container
//   --ind-w / --ind-h  its size; --ind-s its width / 100 (underlines scale a 100px bar)
// `data-ind` on the container says what the indicator is doing:
//   "static"  placed without a transition (first paint, resize, font load)
//   "on"      slides (transition) to the next active item
//   "none"    nothing is selected: hidden
// Moves only animate when the selection changes; anything else (a resize, a
// font finishing loading, a count changing) puts the indicator in place at once.

const KINDS = [
    { root: '.ls-segmented', item: 'button', active: '[aria-pressed="true"], .is-active' },
    { root: '.ls-interval', item: 'button', active: '.is-active, [aria-checked="true"]' },
    { root: '.ls-tabs', item: '.ls-tab', active: '.is-active, [aria-selected="true"]' },
    { root: '.ls-nav', item: '.ls-nav-item', active: '.is-active' },
];

const ROOTS = KINDS.map(kind => kind.root).join(', ');
const WATCHED = ['class', 'aria-pressed', 'aria-selected', 'aria-checked', 'hidden'];

const states = new Map(); // root element -> { kind, active, values, timer }
let resizeObserver = null;

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const kindOf = (root) => KINDS.find(kind => root.matches(kind.root));

const itemsOf = (root, kind) => Array.from(root.querySelectorAll(kind.item))
    .filter(item => item.closest(ROOTS) === root);

const setVars = (root, values) => {
    Object.entries(values).forEach(([name, value]) => root.style.setProperty(name, value));
};

const setMode = (root, mode) => {
    if (root.dataset.ind !== mode) root.dataset.ind = mode;
};

// Turn transitions back on once the instant placement has been drawn
const enableLater = (root, state) => {
    cancelAnimationFrame(state.timer);
    state.timer = requestAnimationFrame(() => {
        state.timer = requestAnimationFrame(() => {
            if (root.dataset.ind === 'static') root.dataset.ind = 'on';
        });
    });
};

const update = (root) => {
    const state = states.get(root);
    if (!state) return;
    if (!root.isConnected) {
        forget(root);
        return;
    }
    const items = itemsOf(root, state.kind);
    const active = items.find(item => item.matches(state.kind.active) && !item.hidden);
    const box = root.getBoundingClientRect();
    // Hidden (display: none, collapsed drawer): place it instantly once it shows
    if (!active || box.width === 0 || box.height === 0) {
        if (!active) setMode(root, 'none');
        state.active = null;
        return;
    }
    const rect = active.getBoundingClientRect();
    // Ignore any transform on the way (a modal popping in scales its content)
    const scale = root.offsetWidth ? box.width / root.offsetWidth : 1;
    const x = (rect.left - box.left) / scale - root.clientLeft + root.scrollLeft;
    const y = (rect.top - box.top) / scale - root.clientTop + root.scrollTop;
    const w = rect.width / scale;
    const h = rect.height / scale;
    const values = {
        '--ind-x': `${x}px`,
        '--ind-y': `${y}px`,
        '--ind-w': `${w}px`,
        '--ind-h': `${h}px`,
        '--ind-s': `${w / 100}`,
    };
    const changed = Object.keys(values).some(name => state.values[name] !== values[name]);
    const slide = state.active !== null && state.active !== active && !reducedMotion();
    state.active = active;
    if (slide) {
        cancelAnimationFrame(state.timer);
        setMode(root, 'on');
    } else if (changed || root.dataset.ind !== 'on') {
        setMode(root, 'static');
        enableLater(root, state);
    }
    if (changed) {
        setVars(root, values);
        state.values = values;
    }
};

const observeItems = (root, state) => {
    if (!resizeObserver) return;
    itemsOf(root, state.kind).forEach(item => resizeObserver.observe(item));
};

const register = (root) => {
    if (states.has(root)) return;
    const kind = kindOf(root);
    if (!kind) return;
    const state = { kind, active: null, values: {}, timer: 0 };
    states.set(root, state);
    // Mark it before measuring: the active item hands its background to the
    // indicator without a transition, so nothing fades on first paint
    setMode(root, 'static');
    resizeObserver?.observe(root);
    observeItems(root, state);
    update(root);
};

function forget(root) {
    const state = states.get(root);
    if (!state) return;
    cancelAnimationFrame(state.timer);
    resizeObserver?.unobserve(root);
    itemsOf(root, state.kind).forEach(item => resizeObserver?.unobserve(item));
    states.delete(root);
}

const updateAll = () => states.forEach((_, root) => update(root));

const registerWithin = (node) => {
    if (node.nodeType !== 1) return;
    if (node.matches(ROOTS)) register(node);
    node.querySelectorAll(ROOTS).forEach(register);
};

const onMutations = (mutations) => {
    const dirty = new Set();
    let removed = false;
    mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(registerWithin);
            if (mutation.removedNodes.length) removed = true;
        }
        const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        const root = target?.closest(ROOTS);
        if (root && states.has(root)) {
            dirty.add(root);
            if (mutation.type === 'childList') observeItems(root, states.get(root));
        }
    });
    if (removed) {
        states.forEach((_, root) => {
            if (!root.isConnected) forget(root);
        });
    }
    dirty.forEach(update);
};

const onResize = (entries) => {
    const dirty = new Set();
    entries.forEach(entry => {
        const root = entry.target.matches(ROOTS) ? entry.target : entry.target.closest(ROOTS);
        if (root && states.has(root)) dirty.add(root);
    });
    dirty.forEach(update);
};

let installed = false;

export default function installSlidingIndicators() {
    if (installed || typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
    installed = true;
    if (typeof ResizeObserver !== 'undefined') resizeObserver = new ResizeObserver(onResize);
    new MutationObserver(onMutations).observe(document.documentElement, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: WATCHED,
    });
    registerWithin(document.body);
    window.addEventListener('resize', updateAll, { passive: true });
    if (document.fonts) {
        document.fonts.ready.then(updateAll);
        document.fonts.addEventListener?.('loadingdone', updateAll);
    }
}
